import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { selectActiveForce, useForceStore } from "@/store/forceStore";
import { validateForce } from "@/utils/validation";

/**
 * Sticky validation banner for mobile.
 * Shows error/warning count at the bottom of the viewport.
 * Tap to expand and see the full error list.
 */
export function ValidationBanner() {
	const force = useForceStore(selectActiveForce);
	const errors = useMemo(() => (force ? validateForce(force) : []), [force]);
	const [expanded, setExpanded] = useState(false);

	if (!force) return null;

	const errorCount = errors.filter((e) => e.type === "error").length;
	const warningCount = errors.filter((e) => e.type === "warning").length;

	// Nothing to show
	if (errors.length === 0) {
		return (
			<div className="fixed bottom-0 left-0 right-0 z-30 bg-accent-primary/10 border-t border-accent-primary/30 px-4 py-2 text-center">
				<span className="text-xs font-medium text-accent-primary">Force is valid</span>
			</div>
		);
	}

	return (
		<div className="fixed bottom-0 left-0 right-0 z-30 bg-bg-secondary border-t border-border-default shadow-lg">
			{/* Summary bar — always visible, tap to expand */}
			<button
				type="button"
				onClick={() => setExpanded(!expanded)}
				className="w-full flex items-center justify-between px-4 py-2.5 cursor-pointer"
			>
				<div className="flex items-center gap-2">
					<span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
						Validation
					</span>
					{errorCount > 0 && (
						<Badge variant="red" size="sm">
							{errorCount} error{errorCount !== 1 ? "s" : ""}
						</Badge>
					)}
					{warningCount > 0 && (
						<Badge variant="amber" size="sm">
							{warningCount} warning{warningCount !== 1 ? "s" : ""}
						</Badge>
					)}
				</div>
				<span className="text-text-muted text-sm">{expanded ? "▾" : "▴"}</span>
			</button>

			{/* Expanded error list */}
			{expanded && (
				<div className="px-4 pb-3 max-h-48 overflow-y-auto space-y-1 border-t border-border-default">
					{errors.map((err, idx) => (
						<div
							key={`${err.rule}-${err.targetId}-${idx}`}
							className={`text-xs px-2 py-1.5 rounded ${
								err.type === "error"
									? "bg-accent-danger/10 text-accent-danger"
									: "bg-accent-secondary/10 text-accent-secondary"
							}`}
						>
							{err.message}
						</div>
					))}
				</div>
			)}
		</div>
	);
}
